import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ColumnsType } from 'antd/es/table';
import { Input, DatePicker, Button, Badge, Select, Modal, Drawer, Tag, Tooltip, InputNumber, Switch } from 'antd';
import { EditOutlined, CheckOutlined, CloseOutlined, SyncOutlined, PauseCircleOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { notificationController } from 'controllers/notificationController';
import moment from 'moment-timezone';
import { getAllDeliveriesAction, getSectorsDataAction, updateDeliveryStatusAction, updateClientBalanceAction } from '@app/store/slices/userSlice';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import styled from 'styled-components';
import { BaseSpace } from '@app/components/common/BaseSpace/BaseSpace';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { useNavigate, useParams } from 'react-router-dom';
import { AssignRiderTable } from '../Assign-Rider';
import { ChoosePushNotificationTable } from '../../manage-push-notifications/ChoosePushNotificationTable';
import config from '@app/config';

const { RangePicker } = DatePicker;
const { Option } = Select;

const EditableBalanceWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BalanceText = styled.span`
  cursor: pointer;
  &:hover {
    color: #1890ff;
  }
`;

export const BasicTable: React.FC = () => {
  const [tableKey, setTableKey] = useState(0);

  // Get default delivery date - always today
  const getDefaultDeliveryDate = () => {
    const now = moment.tz("Asia/Kolkata");

    // Return date range for today
    const startOfDay = now.clone().startOf('day');
    const endOfDay = now.clone().endOf('day');

    return [startOfDay.toDate(), endOfDay.toDate()];
  };

  // Load filters from localStorage or use defaults
  const loadFiltersFromStorage = () => {
    try {
      const savedFilters = localStorage.getItem('deliveryTableFilters');
      if (savedFilters) {
        const parsed = JSON.parse(savedFilters);
        // Convert date strings back to Date objects if they exist
        if (parsed.deliveryDateRange && parsed.deliveryDateRange.length === 2) {
          parsed.deliveryDateRange = [
            new Date(parsed.deliveryDateRange[0]),
            new Date(parsed.deliveryDateRange[1])
          ];
        }
        // Always override the date range with today's date on page load
        parsed.deliveryDateRange = getDefaultDeliveryDate();
        // Always override the status with 'Scheduled' on page load
        parsed.deliveryStatus = 'Scheduled';
        return parsed;
      }
    } catch (error) {
      console.error('Error loading filters from localStorage:', error);
    }

    // Return default filters with today's date and Scheduled status
    return {
      name: '',
      deliveryDateRange: getDefaultDeliveryDate(),
      deliveryStatus: 'Scheduled',
      phone: '',
      order_type: '',
      orderId: '',
      subscription_id: '',
      sector: '',
      orderDateRange: []
    };
  };

  // Save filters to localStorage
  const saveFiltersToStorage = (filters: any) => {
    try {
      localStorage.setItem('deliveryTableFilters', JSON.stringify(filters));
    } catch (error) {
      console.error('Error saving filters to localStorage:', error);
    }
  };
  // Initialize filters from localStorage or defaults
  const initialFilters = loadFiltersFromStorage();

  // Temporary filters (what user types/selects)
  const [tempFilters, setTempFilters] = useState(initialFilters);

  // Applied filters (what's actually sent to API and shown as tags)
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [sorterState, setSorterState] = useState<{ sortBy: string; sortOrder: string }>({ sortBy: 'date', sortOrder: 'desc' });
  const [tableData, setTableData] = useState({ data: [], loading: false, total: 0 });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(false);
  const [notificationDrawerOpen, setNotificationDrawerOpen] = useState(false);
  const dispatch = useAppDispatch();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedDeliveries, setSelectedDeliveries] = useState<any[]>([]);
  const [sectors, setSectors] = useState<React.Key[]>([]);
  const [isBulkAssign, setIsBulkAssign] = useState(false);
  const [isBulkNotification, setIsBulkNotification] = useState(false);
  const [editingBalanceOrderId, setEditingBalanceOrderId] = useState<string | null>(null);
  const [editingBalance, setEditingBalance] = useState<number>(0);
  const [editingClientId, setEditingClientId] = useState<string | null>(null);
  const [proofModal, setProofModal] = useState<{ open: boolean; filePath: string | null }>({ open: false, filePath: null });

  // Auto-refresh state
  const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(true);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);
  const [countdown, setCountdown] = useState(5);
  const autoRefreshIntervalRef = useRef<any>(null);
  const countdownIntervalRef = useRef<any>(null);

  let navigate = useNavigate();
  const { id } = useParams();
  // Fetch data with updated filters and pagination
  const fetchData = () => {
    setTableData((prev) => ({ ...prev, loading: true }));

    // Build params, filtering out empty values
    const params: any = {
      ...appliedFilters,
      sortBy: sorterState.sortBy,
      sortOrder: sorterState.sortOrder,
      deliveryDateRange: appliedFilters.deliveryDateRange?.length
        ? [
          moment.tz(moment(appliedFilters.deliveryDateRange[0]).format('YYYY-MM-DD'), "Asia/Kolkata").startOf('day').toISOString(),
          moment.tz(moment(appliedFilters.deliveryDateRange[1]).format('YYYY-MM-DD'), "Asia/Kolkata").endOf('day').toISOString(),
        ]
        : [],
      orderDateRange: appliedFilters.orderDateRange?.length
        ? [
          moment.tz(moment(appliedFilters.orderDateRange[0]).format('YYYY-MM-DD'), "Asia/Kolkata").startOf('day').toISOString(),
          moment.tz(moment(appliedFilters.orderDateRange[1]).format('YYYY-MM-DD'), "Asia/Kolkata").endOf('day').toISOString(),
        ]
        : [],
      current: pagination.current,
      pageSize: pagination.pageSize,
    };

    // Remove empty deliveryStatus from params (don't send it if cleared)
    if (!params.deliveryStatus || params.deliveryStatus === '') {
      delete params.deliveryStatus;
    }

    // Debug: Log the date ranges being sent to API
    if (appliedFilters.deliveryDateRange && appliedFilters.deliveryDateRange.length > 0) {
      console.log('Delivery Date Range being sent to API:', {
        original: appliedFilters.deliveryDateRange,
        startISO: params.deliveryDateRange[0],
        endISO: params.deliveryDateRange[1]
      });
    }

    dispatch(getAllDeliveriesAction(params)).unwrap()
      .then((response) => {
        setTableData({
          data: response.data,
          loading: false,
          total: response.total,
        });
      })
      .catch((error) => {
        notificationController.error({ message: error.message || 'Failed to fetch data' });
        setTableData((prev) => ({ ...prev, loading: false }));
      });

    dispatch(getSectorsDataAction()).unwrap()
      .then((response) => {
        setSectors(response.sectors);
      })
      .catch((error) => {
        notificationController.error({ message: error.message || 'Failed to fetch data' });
        setTableData((prev) => ({ ...prev, loading: false }));
      });

  };

  // Silent fetch for auto-refresh (no loading spinner)
  const silentFetch = useCallback(() => {
    const params: any = {
      ...appliedFilters,
      sortBy: sorterState.sortBy,
      sortOrder: sorterState.sortOrder,
      deliveryDateRange: appliedFilters.deliveryDateRange?.length
        ? [
          moment.tz(moment(appliedFilters.deliveryDateRange[0]).format('YYYY-MM-DD'), "Asia/Kolkata").startOf('day').toISOString(),
          moment.tz(moment(appliedFilters.deliveryDateRange[1]).format('YYYY-MM-DD'), "Asia/Kolkata").endOf('day').toISOString(),
        ]
        : [],
      orderDateRange: appliedFilters.orderDateRange?.length
        ? [
          moment.tz(moment(appliedFilters.orderDateRange[0]).format('YYYY-MM-DD'), "Asia/Kolkata").startOf('day').toISOString(),
          moment.tz(moment(appliedFilters.orderDateRange[1]).format('YYYY-MM-DD'), "Asia/Kolkata").endOf('day').toISOString(),
        ]
        : [],
      current: pagination.current,
      pageSize: pagination.pageSize,
    };

    if (!params.deliveryStatus || params.deliveryStatus === '') {
      delete params.deliveryStatus;
    }

    dispatch(getAllDeliveriesAction(params)).unwrap()
      .then((response) => {
        setTableData({
          data: response.data,
          loading: false,
          total: response.total,
        });
        setLastRefreshTime(new Date());
      })
      .catch(() => {
        // Silently fail on auto-refresh errors
      });
  }, [appliedFilters, pagination, sorterState, dispatch]);



  useEffect(() => {
    function hideError(e) {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        const resizeObserverErrDiv = document.getElementById(
          'webpack-dev-server-client-overlay-div'
        );
        const resizeObserverErr = document.getElementById(
          'webpack-dev-server-client-overlay'
        );
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute('style', 'display: none');
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute('style', 'display: none');
        }
      }
    }

    window.addEventListener('error', hideError)
    return () => {
      window.addEventListener('error', hideError)
    }
  }, [])

  // Trigger fetch when filters or pagination change
  useEffect(() => {
    fetchData();
    setLastRefreshTime(new Date());
    setCountdown(300);
  }, [appliedFilters, pagination, sorterState]);

  // Auto-refresh interval
  useEffect(() => {
    if (autoRefreshIntervalRef.current) {
      clearInterval(autoRefreshIntervalRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    if (isAutoRefreshEnabled) {
      setCountdown(300);

      countdownIntervalRef.current = setInterval(() => {
        setCountdown((prev) => (prev <= 1 ? 300 : prev - 1));
      }, 1000);

      autoRefreshIntervalRef.current = setInterval(() => {
        silentFetch();
        setCountdown(300);
      }, 300000);
    }

    return () => {
      if (autoRefreshIntervalRef.current) clearInterval(autoRefreshIntervalRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [isAutoRefreshEnabled, silentFetch]);

  // Handle temporary filter changes (while user types)
  const handleFilterChange = (filterName: string, value: any) => {
    setTempFilters((prev) => ({ ...prev, [filterName]: value }));
  };

  // Apply filters (when user clicks Search button)
  const applyFilters = () => {
    setAppliedFilters(tempFilters);
    saveFiltersToStorage(tempFilters); // Save to localStorage
    setPagination({ current: 1, pageSize: pagination.pageSize }); // Reset to first page
  };

  // Handle individual filter removal from tags
  const handleRemoveFilter = (filterName: string) => {
    let defaultValue;
    if (filterName === 'deliveryDateRange' || filterName === 'orderDateRange') {
      defaultValue = [];
    } else {
      // For all other filters including status, clear them (empty string)
      defaultValue = '';
    }

    // Use functional state updates to ensure proper state handling
    setAppliedFilters((prev) => {
      const newFilters = { ...prev, [filterName]: defaultValue };
      return newFilters;
    });

    setTempFilters((prev) => {
      const newFilters = { ...prev, [filterName]: defaultValue };
      // Save to localStorage
      saveFiltersToStorage(newFilters);
      return newFilters;
    });

    setPagination({ current: 1, pageSize: pagination.pageSize }); // Reset to first page
  };

  // Get applied filters for display
  const getAppliedFiltersForDisplay = () => {
    const filtersForDisplay = [];

    if (appliedFilters.name) {
      filtersForDisplay.push({ key: 'name', label: 'Name', value: appliedFilters.name });
    }

    if (appliedFilters.phone) {
      filtersForDisplay.push({ key: 'phone', label: 'Phone', value: appliedFilters.phone });
    }

    if (appliedFilters.orderId) {
      filtersForDisplay.push({ key: 'orderId', label: 'Order ID', value: appliedFilters.orderId });
    }

    if (appliedFilters.subscription_id) {
      filtersForDisplay.push({ key: 'subscription_id', label: 'Subscription ID', value: appliedFilters.subscription_id });
    }

    if (appliedFilters.order_type) {
      filtersForDisplay.push({ key: 'order_type', label: 'Order Type', value: appliedFilters.order_type });
    }

    if (appliedFilters.deliveryStatus) {
      filtersForDisplay.push({ key: 'deliveryStatus', label: 'Status', value: appliedFilters.deliveryStatus });
    }

    if (appliedFilters.sector) {
      filtersForDisplay.push({ key: 'sector', label: 'Sector', value: appliedFilters.sector });
    }

    if (appliedFilters.deliveryDateRange && appliedFilters.deliveryDateRange.length === 2) {
      const startDate = moment(appliedFilters.deliveryDateRange[0]).format('DD-MMM-YYYY');
      const endDate = moment(appliedFilters.deliveryDateRange[1]).format('DD-MMM-YYYY');
      filtersForDisplay.push({
        key: 'deliveryDateRange',
        label: 'Delivery Date',
        value: `${startDate} to ${endDate}`
      });
    }

    if (appliedFilters.orderDateRange && appliedFilters.orderDateRange.length === 2) {
      const startDate = moment(appliedFilters.orderDateRange[0]).format('DD-MMM-YYYY');
      const endDate = moment(appliedFilters.orderDateRange[1]).format('DD-MMM-YYYY');
      filtersForDisplay.push({
        key: 'orderDateRange',
        label: 'Order Date',
        value: `${startDate} to ${endDate}`
      });
    }

    return filtersForDisplay;
  };

  // Render applied filters as tags
  const renderAppliedFilters = () => {
    const appliedFiltersForDisplay = getAppliedFiltersForDisplay();

    if (appliedFiltersForDisplay.length === 0) {
      return null;
    }

    return (
      <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
        <div style={{ marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
          Applied Filters:
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {appliedFiltersForDisplay.map((filter) => (
            <Tag
              key={filter.key}
              closable
              onClose={() => handleRemoveFilter(filter.key)}
              color="blue"
              style={{ margin: 0 }}
            >
              <strong>{filter.label}:</strong> {filter.value}
            </Tag>
          ))}
        </div>
      </div>
    );
  };

  // Handle table pagination and sorter change
  const handleTableChange = (paginationInfo: any, tableFilters: any, sorter: any) => {
    setPagination({ current: paginationInfo.current, pageSize: paginationInfo.pageSize });
    if (sorter && sorter.field) {
      setSorterState({
        sortBy: sorter.field,
        sortOrder: sorter.order === 'ascend' ? 'asc' : 'desc',
      });
    }
  };
  const handleStatusChange = (record, newStatus) => {
    const isAlaCarteCancel =
      record?.order_type === 'A La Carte' && ['Cancelled', 'Forcefully Cancelled'].includes(newStatus);
    Modal.confirm({
      title: 'Are you sure?',
      content: isAlaCarteCancel
        ? `Are you sure to perform this action? Mark status as ${newStatus}. The order amount will be refunded to the customer's wallet.`
        : `Are you sure to perform this action? Mark status as ${newStatus}`,
      onOk: () => {
        // Call API to update the status
        dispatch(
          updateDeliveryStatusAction({ deliveryId: record._id, status: newStatus }) // Replace with the actual action for updating status
        )
          .unwrap()
          .then((resp) => {

            if (resp.success) {
              notificationController.success({
                message: resp?.refunded ? resp.message : `Status updated to ${newStatus}`,
              });
              fetchData();
            }
            else {
              notificationController.warning({
                message: `${resp?.message || "Something went wrong"}`,
              });
            }
          })
          .catch((error) => {
            notificationController.error({
              message: error.message || 'Failed to update status',
            });
          });
      },
    });
  };

  // Handle balance edit
  const handleEditBalance = (record: any) => {
    if (record?.client?._id && record?.orderId) {
      setEditingBalanceOrderId(record.orderId);
      setEditingClientId(record.client._id);
      setEditingBalance(record.client.balance || 0);
    }
  };

  // Handle balance save
  const handleSaveBalance = (record: any) => {
    if (!editingClientId) return;

    Modal.confirm({
      title: 'Confirm Balance Update',
      content: `Are you sure you want to update the wallet balance from ₹${record.client.balance} to ₹${editingBalance}?`,
      onOk: () => {
        dispatch(updateClientBalanceAction({ clientId: editingClientId, balance: editingBalance }))
          .unwrap()
          .then((response) => {
            if (response.success) {
              notificationController.success({ message: 'Balance updated successfully' });
              setEditingBalanceOrderId(null);
              setEditingClientId(null);
              fetchData();
            } else {
              notificationController.error({ message: response.message || 'Failed to update balance' });
            }
          })
          .catch((error) => {
            notificationController.error({ message: error || 'Failed to update balance' });
          });
      },
    });
  };

  // Handle balance cancel
  const handleCancelBalance = () => {
    setEditingBalanceOrderId(null);
    setEditingClientId(null);
    setEditingBalance(0);
  };
  const columns: ColumnsType<any> = [

    {
      title: 'Delivery Address Name',
      dataIndex: 'name',
      render: (text, record) => <span>{record?.address?.customer_name || 'Not available'}</span>,
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      render: (text, record) => <span>{record?.client?.phone || 'Not available'}</span>,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Phone"
            value={selectedKeys[0]}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
              handleFilterChange('phone', value); // Update the filter state
            }}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => {
              confirm();
              applyFilters();
            }}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),

    },
    {
      title: 'Wallet Balance',
      dataIndex: 'client.balance',
      render: (text, record) => {
        const isEditing = editingBalanceOrderId === record?.orderId;

        return (
          <EditableBalanceWrapper>
            {isEditing ? (
              <>
                <InputNumber
                  value={editingBalance}
                  onChange={(value) => setEditingBalance(value || 0)}
                  style={{ width: 120 }}
                  min={0}
                  precision={2}
                  autoFocus
                />
                <Tooltip title="Save">
                  <Button
                    type="primary"
                    size="small"
                    icon={<CheckOutlined />}
                    onClick={() => handleSaveBalance(record)}
                  />
                </Tooltip>
                <Tooltip title="Cancel">
                  <Button
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={handleCancelBalance}
                  />
                </Tooltip>
              </>
            ) : (
              <>
                <BalanceText>{record?.client?.balance || 0}</BalanceText>
                <Tooltip title="Edit Balance">
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => handleEditBalance(record)}
                  />
                </Tooltip>
              </>
            )}
          </EditableBalanceWrapper>
        );
      },
    },
    {
      title: 'Product Name',
      dataIndex: 'product_name',
      render: (text, record) =>
        record.order_type == 'Subscription' ?
          (<span>{record?.product?.name || 'Not available'}
          </span>)

          :
          record?.product?.map((x, idx) => (
            <span key={idx}>
              {x.name} -- {x.cart_quantity} qty
              {idx < record.product.length - 1 && ', '}
            </span>
          )),


      // onFilter: (value, record) => record?.product?.name.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Product Size',
      dataIndex: 'product_size',
      render: (text, record) => <span>{
        record.order_type !== 'A La Carte' ?
          record?.product?.isVariantEnabled ?
            record?.selectedOption?.quantity + ' ' + record?.selectedOption?.unit :
            record?.product?.quantity + ' ' + record?.product?.unit
          :
          "N/A"}</span>,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      render: (text, record) => <span>Rs {calculateAmountOfDelivery(record)}</span>,
    },
    {
      title: 'Delivery Date',
      dataIndex: 'date',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      defaultSortOrder: 'descend',
      render: (text, record) => (
        <span>
          {record.date ? moment(record?.date).tz("Asia/Kolkata").format("DD-MMM-YYYY") : ""}
        </span>
      ),

      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <RangePicker
            value={selectedKeys.length === 2 ? (selectedKeys as any) : (
              tempFilters.deliveryDateRange && tempFilters.deliveryDateRange.length === 2
                ? [moment(tempFilters.deliveryDateRange[0]), moment(tempFilters.deliveryDateRange[1])] as any
                : null
            )}
            onChange={(dates) => {
              setSelectedKeys(dates || []);
              handleFilterChange('deliveryDateRange', dates || []); // Update the filter state
            }}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => {
              confirm();
              applyFilters();
            }}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
    },

    {
      title: 'Delivery Slot',
      dataIndex: 'slot_id',
      onHeaderCell: () => ({ style: { minWidth: 180 } }),
      onCell: () => ({ style: { minWidth: 180 } }),
      render: (_text: any, record: any) => {
        const slot = record?.slot_id;
        if (!slot || typeof slot !== 'object') {
          return <span style={{ color: '#bbb' }}>—</span>;
        }
        const label = slot.label || slot.name || 'Slot';
        const time = (slot.start_time && slot.end_time) ? `${slot.start_time} – ${slot.end_time}` : '';
        return (
          <span>
            <Badge color="#008cdc" text={label} />
            {time && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{time}</div>}
          </span>
        );
      },
    },

    {
      title: 'Order Date',
      dataIndex: 'createdAt',
      render: (text, record) => (
        <span>
          {record.createdAt ? moment(record?.createdAt).tz("Asia/Kolkata").format("DD-MMM-YYYY") : ""}
        </span>
      ),

      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <RangePicker
            value={selectedKeys.length === 2 ? (selectedKeys as any) : (
              tempFilters.orderDateRange && tempFilters.orderDateRange.length === 2
                ? [moment(tempFilters.orderDateRange[0]), moment(tempFilters.orderDateRange[1])] as any
                : null
            )}
            onChange={(dates) => {
              setSelectedKeys(dates || []);
              handleFilterChange('orderDateRange', dates || []); // Update the filter state
            }}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => {
              confirm();
              applyFilters();
            }}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
    },

    {
      title: 'Quantity',
      dataIndex: 'quantity',
      render: (text) => <span>{text || 'Not available'}</span>,
    },
    {
      title: 'Rider',
      dataIndex: 'rider_info',
      render: (text, record) => <span>{record.rider_info ? (record.rider_info?.firstName + ' ' + record.rider_info?.lastName) : 'N/A'}</span>,
    },
    {
      title: 'Order Type',
      dataIndex: 'order_type',
      render: (text) => <span>{text || 'Not available'}</span>,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Select
            value={selectedKeys[0] || ''}
            onChange={(value) => {
              setSelectedKeys(value ? [value] : []);
              handleFilterChange('order_type', value); // Update the filter state
            }}
            style={{ width: 188, marginBottom: 8 }}
            placeholder="Select Order Type"
          >
            <Option value="A La Carte">A La Carte</Option>
            <Option value="Subscription">Subscription</Option>

          </Select>
          <Button
            type="primary"
            onClick={() => {
              confirm();
              applyFilters();
            }}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
    },

    {
      title: 'Status',
      dataIndex: 'status',
      render: (text, record) => <span>{text}</span>,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Select
            value={selectedKeys[0] || tempFilters.deliveryStatus || ''}
            onChange={(value) => {
              setSelectedKeys(value ? [value] : []);
              handleFilterChange('deliveryStatus', value); // Update the filter state
            }}
            style={{ width: 188, marginBottom: 8 }}
            placeholder="Select Status"
          >
            <Option value="Scheduled">Scheduled</Option>
            <Option value="Delivered">Delivered</Option>
            <Option value="Pending">Pending</Option>
            <Option value="Skipped">Skipped</Option>
            <Option value="Refunded">Refunded</Option>
          </Select>
          <Button
            type="primary"
            onClick={() => {
              confirm();
              applyFilters();
            }}
            size="small"
            style={{ width: 90, marginRight: 8, marginTop: '10px' }}
          >
            Search
          </Button>
          <Button onClick={() => {
            setSelectedKeys(['Scheduled']);
            handleFilterChange('deliveryStatus', 'Scheduled');
            clearFilters();
            applyFilters();
          }} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
    },
    {
      title: 'Sector',
      dataIndex: 'sector',
      render: (text, record) => <span>{record?.address?.sector}</span>,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Select
            value={selectedKeys[0] || ''}
            onChange={(value) => {
              setSelectedKeys(value ? [value] : []);
              handleFilterChange('sector', value); // Update the filter state
            }}
            style={{ width: 188, marginBottom: 8 }}
            placeholder="Select Sector"
          >
            {sectors && sectors.map((sector, idx) => (
              <Option key={idx} value={sector}>{sector}</Option>
            ))}

          </Select>
          <Button
            type="primary"
            onClick={() => {
              confirm();
              applyFilters();
            }}
            size="small"
            style={{ width: 90, marginRight: 8, marginTop: '10px' }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
    },
    {
      title: 'Address',
      dataIndex: 'address',
      render: (text, record) => <span>{record?.address?.house_no}, {record?.address?.sector}, {record?.address?.address},{record?.address?.type}</span>,

    },
    {
      title: 'Delivery Instructions',
      dataIndex: 'delivery_instructions',
      render: (text, record) => {
        const instructions = record?.delivery_instructions;
        if (!instructions || !Array.isArray(instructions) || instructions.length === 0) {
          return <span>Not Available</span>;
        }
        return (
          <div>
            {instructions.map((instruction, index) => (
              <div key={index} style={{ marginBottom: '4px' }}>
                • {instruction}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      width: '15%',
      render: (text: string, record: any) => (
        <div>
          <div style={{ display: 'block', justifyContent: 'space-between' }}>
            <label title='Account Status'>Status</label>
            <Select

              defaultValue={record.status}
              value={record.status}
              style={{ width: '100%' }}
              onChange={(value) => handleStatusChange(record, value)}
            >
              <Option value="Scheduled" hidden>Scheduled</Option>
              <Option value="Cancelled" hidden>Cancelled</Option>
              <Option value="Completed" hidden>Completed</Option>
              <Option value="Skipped">Skipped</Option>
              <Option value="Forcefully Cancelled">Forcefully Cancelled</Option>
              <Option value="Forcefully Completed">Forcefully Completed</Option>
            </Select>
          </div>

          <Button
            disabled={record?.status == 'Delivered'}
            type="primary"
            style={{
              marginTop: '10px',
              display: (isBulkAssign || isBulkNotification) ? 'none' : ''
            }}
            onClick={() => { setSelectedDelivery(record); setDrawerOpen(true) }}
          >
            {!record.rider_info ? 'Choose' : 'Edit'} Rider
          </Button>

          <Button
            type="default"
            disabled={!record?.file_info?.filePath}
            style={{ marginTop: '8px', width: '100%' }}
            onClick={() => setProofModal({ open: true, filePath: record.file_info.filePath })}
          >
            📷 View Proof
          </Button>
        </div>
      ),
    },

  ];
  const handleBulkAssign = () => {
    setIsBulkAssign(true);
  };

  const handleCancelBulkAssign = () => {
    setIsBulkAssign(false);
    setSelectedRowKeys([]);
  };

  const handleBulkNotification = () => {
    setIsBulkNotification(true);
  };

  const handleCancelBulkNotification = () => {
    setIsBulkNotification(false);
    setSelectedRowKeys([]);
    setSelectedDeliveries([]);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[]) => {
      setSelectedRowKeys(selectedKeys);
      // Update selected deliveries based on selected keys
      const selectedDeliveryData = tableData.data.filter((delivery: any) =>
        selectedKeys.includes(delivery.orderId)
      );
      setSelectedDeliveries(selectedDeliveryData);
    },
  };

  const calculateAmountOfDelivery = (record: any) => {
    if (!record) return 0;
    if (record.order_type == 'Subscription') {
      const isHistorical = record.status && record.status !== 'Scheduled';

      if (isHistorical) {
        // For historic deliveries (Delivered, Forcefully Completed, Cancelled, Skipped, Refunded, etc.):
        // Preserve historic locked product_price or date amount, do not overwrite with latest product sale_price
        if (record.product_price !== undefined && record.product_price !== null) {
          return record.product_price * (record.quantity || 1);
        }
        if (record.amount !== undefined && record.amount !== null && record.amount > 0) {
          return record.amount;
        }
      }

      // For Scheduled (future/ongoing) deliveries: reflect current updated product/variant price
      if (record?.product?.isVariantEnabled) {
        return (record.selectedOption?.sale_price || record.product_price || 0) * (record.quantity || 1);
      } else {
        return (record.product?.sale_price || record.product_price || 0) * (record.quantity || 1);
      }
    } else {
      return record.amount ?? 0;
    }
  };

  const onDrawerClose = () => {
    setDrawerOpen(false);
  }

  const onNotificationDrawerClose = () => {
    setNotificationDrawerOpen(false);
  }
  console.log('appliedFilters', appliedFilters, 'tempFilters', tempFilters)

  return (
    <>
      <Drawer
        title="Basic Drawer"
        placement={'right'}
        closable={false}
        onClose={onDrawerClose}
        open={drawerOpen}
        key={'right'}
        size={'large'}

      >
        <AssignRiderTable
          selectedDelivery={selectedDelivery}
          onDrawerClose={onDrawerClose}
          fetchDeliveriesData={fetchData}
          selectedRowKeys={selectedRowKeys}
          isBulkAssign={isBulkAssign}
          handleCancelBulkAssign={handleCancelBulkAssign}
        />
      </Drawer>

      <Drawer
        title="Send Push Notification"
        placement={'right'}
        closable={false}
        onClose={onNotificationDrawerClose}
        open={notificationDrawerOpen}
        key={'notification'}
        size={'large'}
      >
        <ChoosePushNotificationTable
          selectedDeliveries={selectedDeliveries}
          onDrawerClose={onNotificationDrawerClose}
          fetchDeliveriesData={fetchData}
          handleCancelBulkNotification={handleCancelBulkNotification}
        />
      </Drawer>
      <Button
        style={{ marginBottom: '20px' }}
        onClick={() => {
          setTableData((prev) => ({ ...prev, loading: true }));
          const clearedFilters = {
            name: '',
            deliveryDateRange: getDefaultDeliveryDate(), // Reset to default date instead of empty
            deliveryStatus: 'Scheduled', // Always reset to default status
            phone: '',
            order_type: '',
            orderId: '',
            subscription_id: '',
            sector: '',
            orderDateRange: []
          };
          // Ensure status is always 'Scheduled'
          clearedFilters.deliveryStatus = 'Scheduled';

          setTempFilters(clearedFilters);
          setAppliedFilters(clearedFilters);
          saveFiltersToStorage(clearedFilters); // Save to localStorage
          setPagination({ current: 1, pageSize: 10 });
          setTableKey(tableKey + 1);
        }}>Clear Filters</Button>

      {/* Auto-Refresh Timer Pill */}
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '12px',
        padding: '6px 16px',
        backgroundColor: isAutoRefreshEnabled ? '#f0f9ff' : '#f5f5f5',
        border: `1px solid ${isAutoRefreshEnabled ? '#91d5ff' : '#d9d9d9'}`,
        borderRadius: '20px',
        fontSize: '13px',
        marginLeft: 'auto',
        float: 'right' as const,
        marginBottom: '20px',
        verticalAlign: 'middle',
      }}>
        {isAutoRefreshEnabled ? (
          <SyncOutlined spin style={{ color: '#1890ff', fontSize: '14px' }} />
        ) : (
          <PauseCircleOutlined style={{ color: '#999', fontSize: '14px' }} />
        )}
        <span style={{ color: isAutoRefreshEnabled ? '#1890ff' : '#999', fontWeight: 500 }}>
          {isAutoRefreshEnabled ? `Next refresh in: ${Math.floor(countdown / 60)}:${(countdown % 60).toString().padStart(2, '0')}` : 'Auto-refresh paused'}
        </span>
        {lastRefreshTime && (
          <span style={{ color: '#999', fontSize: '12px', borderLeft: '1px solid #d9d9d9', paddingLeft: '10px' }}>
            Last: {moment(lastRefreshTime).format('hh:mm:ss A')}
          </span>
        )}
        <Switch
          size="small"
          checked={isAutoRefreshEnabled}
          onChange={(checked) => setIsAutoRefreshEnabled(checked)}
          checkedChildren={<PlayCircleOutlined />}
          unCheckedChildren={<PauseCircleOutlined />}
        />
      </div>

      {!isBulkAssign && !isBulkNotification ? (
        <>
          <Button type="primary" onClick={() => {
            const newFilters = { ...appliedFilters, deliveryStatus: 'Scheduled' };
            setTempFilters(newFilters);
            setAppliedFilters(newFilters);
            saveFiltersToStorage(newFilters); // Save to localStorage

            setTimeout(() => {
              fetchData();
              handleBulkAssign()
            }, 700);

          }}>
            Bulk Assign
          </Button>
          <Button
            type="default"
            style={{ marginLeft: '10px' }}
            onClick={handleBulkNotification}
          >
            Bulk Push Notification
          </Button>
        </>
      ) : isBulkAssign ? (
        <>
          <Button type="primary" onClick={() => { setDrawerOpen(true); }}>
            Assign Rider to Selected
          </Button>
          <Button onClick={handleCancelBulkAssign}>
            Cancel Bulk Assign
          </Button>
        </>
      ) : (
        <>
          <Button
            type="primary"
            onClick={() => { setNotificationDrawerOpen(true); }}
            disabled={!selectedRowKeys.length}
          >
            Choose Push Notification ({selectedRowKeys.length} selected)
          </Button>
          <Button onClick={handleCancelBulkNotification}>
            Cancel Bulk Notification
          </Button>
        </>
      )}

      {renderAppliedFilters()}

      <BaseTable
        showSizeChanger={true}
        key={tableKey}
        rowKey={(record) => record.orderId}
        columns={columns}
        dataSource={tableData.data}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: tableData.total,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: 2400 }}
        bordered
        rowSelection={(isBulkAssign || isBulkNotification) ? rowSelection : undefined}
        rowClassName={(record) => {
          const balance = record?.client?.balance ?? 0;
          const orderAmount = calculateAmountOfDelivery(record) ?? 0;
          return balance < orderAmount ? 'row-highlight-warning' : '';
        }}
      />

      <Modal
        open={proofModal.open}
        title="Delivery Proof"
        footer={null}
        onCancel={() => setProofModal({ open: false, filePath: null })}
        centered
        width={680}
      >
        {proofModal.filePath && (() => {
          const url = config.API1Url + '/' + proofModal.filePath;
          const isImage = /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(proofModal.filePath);
          return isImage ? (
            <img
              src={url}
              alt="Delivery proof"
              style={{ width: '100%', borderRadius: 8, objectFit: 'contain', maxHeight: 520 }}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <p style={{ marginBottom: 12 }}>This file cannot be previewed inline.</p>
              <Button type="primary" onClick={() => window.open(url, '_blank')}>Open File</Button>
            </div>
          );
        })()}
      </Modal>
    </>
  );
};
